import { HTTP_CP_SignUp_Request } from '@api-new/authservice';
import { ENUM_PropertyInvestmentType, ENUM_SmartSearchBuyPropertyType } from '@api-new/common';
import { MortgageFormValues } from '@app/sign-up/forms/mortgage.form';
import { PropertyFormValues } from '@app/sign-up/forms/property.form';
import { CreateUserFormValues } from '@client-ui/components/forms/create-user-form/create-user.form';
import { SmartSearchFormValues } from '@shared-lib/forms/sign-up-smart-search/smart-search.form';
import { SmartSearchBasicDataModel } from '@shared/store/sign-up/models/smart-search-basic-data.model';
import { signUpClientUserFromToHttpRequestAdapter } from './sign-up-client-user.adapter';
import { signUpMortgageToHttpRequestAdapter } from './sign-up-mortgage.adapter';
import { signUpPropertyToHttpRequestAdapter } from './sign-up-property.adapter';
import {
  signUpSmartSearchBuyPropertyToHttpRequestAdapter,
  signUpSmartSearchCaseToHttpRequestAdapter,
  signUpSmartSearchRemortgageToHttpRequestAdapter,
} from './sign-up-smart-search.adapter';

export const signUpCreationMethodMortgageMonitorAdapter = (
  clientData: CreateUserFormValues,
  property: PropertyFormValues | null,
  mortgage: MortgageFormValues | null,
  companyId: string,
  referralCode: string | null,
): HTTP_CP_SignUp_Request => {
  return {
    clientUser: signUpClientUserFromToHttpRequestAdapter(clientData, referralCode, property, companyId),
    mortgageMonitor: {
      property: signUpPropertyToHttpRequestAdapter(property),
      mortgage: signUpMortgageToHttpRequestAdapter(mortgage),
    },
  };
};

export const signUpCreationMethodRemortgageNowAdapter = (
  clientData: CreateUserFormValues,
  property: PropertyFormValues | null,
  mortgage: MortgageFormValues | null,
  smartSearch: SmartSearchFormValues | null,
  referralCode: string | null,
  companyId: string,
  smartSearchBasicData: SmartSearchBasicDataModel,
): HTTP_CP_SignUp_Request => {
  return {
    clientUser: signUpClientUserFromToHttpRequestAdapter(clientData, referralCode, property, companyId),
    remortgageNow: {
      property: signUpPropertyToHttpRequestAdapter(property),
      mortgage: signUpMortgageToHttpRequestAdapter(mortgage),
      smartSearchCase: signUpSmartSearchCaseToHttpRequestAdapter(smartSearch),
      smartSearch: signUpSmartSearchRemortgageToHttpRequestAdapter(smartSearch, smartSearchBasicData, !!mortgage),
    },
  };
};

export const signUpCreationMethodFirstTimeBuyerAdapter = (
  clientData: CreateUserFormValues,
  smartSearch: SmartSearchFormValues | null,
  referralCode: string | null,
  companyId: string,
  smartSearchBasicData: SmartSearchBasicDataModel,
): HTTP_CP_SignUp_Request => {
  return {
    clientUser: signUpClientUserFromToHttpRequestAdapter(clientData, referralCode, null, companyId),
    firstTimeBuyer: {
      smartSearch: signUpSmartSearchBuyPropertyToHttpRequestAdapter(
        smartSearch,
        smartSearchBasicData,
        ENUM_SmartSearchBuyPropertyType.SMART_SEARCH_BUY_PROPERTY_TYPE_FIRST_TIME_BUYER,
        ENUM_PropertyInvestmentType.PROPERTY_INVESTMENT_TYPE_RESIDENTIAL,
      ),
      smartSearchCase: signUpSmartSearchCaseToHttpRequestAdapter(smartSearch),
    },
  };
};

export const signUpCreationMethodMovingHomeAdapter = (
  clientData: CreateUserFormValues,
  property: PropertyFormValues | null,
  mortgage: MortgageFormValues | null,
  smartSearch: SmartSearchFormValues | null,
  referralCode: string | null,
  companyId: string,
  smartSearchBasicData: SmartSearchBasicDataModel,
): HTTP_CP_SignUp_Request => {
  return {
    clientUser: signUpClientUserFromToHttpRequestAdapter(clientData, referralCode, property, companyId),
    movingHome: {
      movingHomeWithSmartSearch: smartSearch?.mortgageProductId
        ? {
            smartSearch: signUpSmartSearchBuyPropertyToHttpRequestAdapter(
              smartSearch,
              smartSearchBasicData,
              ENUM_SmartSearchBuyPropertyType.SMART_SEARCH_BUY_PROPERTY_TYPE_MOVING_HOME,
              ENUM_PropertyInvestmentType.PROPERTY_INVESTMENT_TYPE_RESIDENTIAL,
            ),
            smartSearchCase: signUpSmartSearchCaseToHttpRequestAdapter(smartSearch),
          }
        : undefined,
      movingHomeWithProperty: !smartSearch?.mortgageProductId
        ? {
            property: signUpPropertyToHttpRequestAdapter(property),
            mortgage: signUpMortgageToHttpRequestAdapter(mortgage),
          }
        : undefined,
    },
  };
};

export const signUpCreationMethodBuyToLetAdapter = (
  clientData: CreateUserFormValues,
  smartSearch: SmartSearchFormValues | null,
  referralCode: string | null,
  companyId: string,
  smartSearchBasicData: SmartSearchBasicDataModel,
): HTTP_CP_SignUp_Request => {
  return {
    clientUser: signUpClientUserFromToHttpRequestAdapter(clientData, referralCode, null, companyId),
    buyToLet: {
      smartSearch: smartSearch
        ? signUpSmartSearchBuyPropertyToHttpRequestAdapter(
            smartSearch,
            smartSearchBasicData,
            ENUM_SmartSearchBuyPropertyType.SMART_SEARCH_BUY_PROPERTY_TYPE_BUY_TO_LET,
            ENUM_PropertyInvestmentType.PROPERTY_INVESTMENT_TYPE_BUY_TO_LET,
          )
        : undefined,
      smartSearchCase: signUpSmartSearchCaseToHttpRequestAdapter(smartSearch),
    },
  };
};

export const signUpCreationMethodLandlordAdapter = (
  clientData: CreateUserFormValues,
  referralCode: string | null,
  companyId: string,
): HTTP_CP_SignUp_Request => {
  return {
    clientUser: signUpClientUserFromToHttpRequestAdapter(clientData, referralCode, null, companyId),
    landlord: {
      declaredNumberOfProperties: clientData.numberOfProperties!,
    },
  };
};
